import classNames from "classnames"
import { Order } from "pages/admin/Orders"
import React from "react"
import { Button, Card, CardBody, Col, Row } from "reactstrap"
import { CurrentTapType } from "./ordersType"
import { OrderStatus } from "./Components/OrderStatus"
import { PaymentState } from "./Components/PaymentStatus"
import OrderActionMenu from "./Components/OrderActionMenu"
import { useTranslation } from "react-i18next"

interface OrderCardProps {
  order: Order
  acceptHandler: (order: Order) => void
  rejectHandler: (order: Order) => void
  viewDetailsHandler: (order: Order) => void
}
const OrderCard: React.FC<OrderCardProps> = ({
  order,
  acceptHandler,
  rejectHandler,
  viewDetailsHandler,
}) => {
  const { t } = useTranslation()

  return (
    <Col xxl="3" xl="4" sm="6">
      <Card className="text-center border border-1 shadow-sm">
        <CardBody className="p-3 border-1 shadow">
          <div className="d-flex justify-content-between align-items-start mb-2 text-start">
            <div className="d-flex justify-content-between align-items-start w-100">
              <div className="text-start">
                <p className="mb-0 text-muted">{order.id}</p>
                <div className="text-muted fw-bold small">
                  {t("orders_dateTime")}:{" "}
                  <span className="fw-normal">{order.createdDate}</span>
                </div>
                <div className="fw-bold text-muted small">
                  {t("orders_totalItems")}:{" "}
                  <span className="text-dark font-size-12 fw-bold">
                    {order.items}
                  </span>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <OrderStatus orderStatus={order.orderStatus} />
                <OrderActionMenu
                  order={order}
                  acceptHandler={acceptHandler}
                  rejectHandler={rejectHandler}
                  viewDetailsHandler={viewDetailsHandler}
                />
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between mb-2">
            <div className="p-2 w-50 me-2 text-start">
              <div className="text-muted small">
                {t("orders_totalOrderValue")}
              </div>
              <div className="fw-bold">{order.orderValue}</div>
            </div>
            <div className="p-2 w-50 ms-2 text-start">
              <div className="text-muted small">{t("orders_payment")}</div>
              <PaymentState payment={order.paymentStatus} className="badge" />
            </div>
          </div>

          {order.orderStatus !== "orders_pending" && (
            <div className="border rounded p-2 d-flex mb-2 align-items-center justify-content-between">
              <div className="d-flex flex-column align-items-start gap-1">
                <div className="text-muted small">
                  {t("orders_deliveryMan")}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <img
                    src="https://placehold.co/24x24/png"
                    alt="avatar"
                    className="rounded-circle"
                    style={{
                      width: "24px",
                      height: "24px",
                    }}
                  />
                  <div className="fw-semibold text-capitalize small">
                    Ahmed Ali
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn bg-success text-white btn-md rounded-circle p-1"
                  style={{ width: "35px", height: "35px" }}
                >
                  <i className="bx bx bx-phone bx-sm" />
                </button>
                <button
                  className="btn btn-light btn-md rounded-circle p-1 bg-white border-success text-success"
                  style={{ width: "35px", height: "35px" }}
                >
                  <i className="bx bx-message bx-sm" />
                </button>
              </div>
            </div>
          )}

          <div className="border rounded p-2 d-flex align-items-center justify-content-between">
            <div className="d-flex flex-column align-items-start gap-1">
              <div className="text-muted small">{t("common_createdBy")}</div>
              <div className="d-flex flex-column align-items-start gap-1">
                <div className="d-flex align-items-center justify-content-start gap-2">
                  <img
                    src="https://placehold.co/24x24/png"
                    alt="avatar"
                    className="rounded-circle"
                    style={{
                      width: "24px",
                      height: "24px",
                    }}
                  />
                  <div className="fw-semibold text-capitalize small">
                    {order.createdBy}
                  </div>
                </div>

                {order.orderStatus === "orders_pending" && (
                  <div className="d-flex align-items-center justify-content-start gap-2">
                    <div
                      className="text-center d-flex align-items-center justify-content-center text-secondary rounded-circle"
                      style={{ width: "24px", height: "24px" }}
                    >
                      <i className="dripicons-location rounded-circle d-flex" />
                    </div>
                    <div className="fw-semibold text-capitalize small">
                      {t("orders_customerAdderssGoesHere")}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-light btn-md rounded-circle p-1 bg-success text-white"
                style={{ width: "35px", height: "35px" }}
              >
                <i className="bx bx bx-phone bx-sm" />
              </button>
              <button
                className="btn btn-light border bg-white text-success border-success btn-md rounded-circle p-1"
                style={{ width: "35px", height: "35px" }}
              >
                <i className="bx bx-message bx-sm" />
              </button>
            </div>
          </div>
          {order.orderStatus === "orders_pending" && (
            <div className="mt-2 d-flex justify-content-space-between align-items-center gap-1">
              <Button
                onClick={() => acceptHandler(order)}
                className="btn bg-gradiant-blue border-0 waves-effect waves-light flex-grow-1 "
              >
                {t("common_accept")}
              </Button>
              <Button
                onClick={() => rejectHandler(order)}
                outline
                color="primary"
                className="btn btn-outline-cyan waves-effect waves-light flex-grow-1 text-cyan bg-cyan-10"
              >
                {t("common_reject")}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    </Col>
  )
}

interface OrdersGridProps {
  orders: Order[]
  acceptHandler: (order: Order) => void
  rejectHandler: (order: Order) => void
  viewDetailsHandler: (order: Order) => void
}

export const OrdersGrid: React.FC<OrdersGridProps> = ({
  orders,
  acceptHandler,
  rejectHandler,
  viewDetailsHandler,
}) => {
  return (
    <Row>
      {orders.map(order => (
        <OrderCard
          key={order.id}
          order={order}
          acceptHandler={acceptHandler}
          rejectHandler={rejectHandler}
          viewDetailsHandler={viewDetailsHandler}
        />
      ))}
    </Row>
  )
}
